<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    offset-y
    max-width="360"
    :data-testid="testId"
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs">
        <base-button
          v-bind="attrs"
          v-on="on"
          secondary
          small
          :label="activatorLabel"
          :data-testid="`${testId}__activator`"
        />
      </slot>
    </template>

    <v-card class="search-checkbox-picker" :data-testid="`${testId}__card`">
      <v-card-title class="search-checkbox-picker__header tableHeaderBg pa-3">
        <span class="font-weight-bold">{{ title }}</span>
        <v-spacer />
        <v-btn icon small @click="closeMenu" :data-testid="`${testId}__close`">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text v-if="moduleFilters.length" class="pa-2 pb-0">
        <div class="search-checkbox-picker__module-filters d-flex flex-wrap gap-2">
          <v-chip
            v-for="mod in moduleFilters"
            :key="mod.value"
            small
            label
            class="noTransform"
            :outlined="moduleFilter !== mod.value"
            :color="moduleFilter === mod.value ? 'primary' : undefined"
            :data-testid="`${testId}__module-${mod.value}`"
            @click="onModuleFilterChange(mod.value)"
          >
            {{ mod.text }}
          </v-chip>
        </div>
      </v-card-text>

      <v-card-text class="pa-2 pb-0">
        <lb-string
          v-model="searchText"
          label="Search"
          hidedetails
          :data-testid="`${testId}__search`"
        />
      </v-card-text>

      <v-card-text class="search-checkbox-picker__list pa-0 ma-0 pt-2">
        <div v-if="showSelectAll" class="px-2 pb-1">
          <v-checkbox
            dense
            hide-details
            class="mt-0"
            :indeterminate="selectAllIndeterminate"
            :input-value="allSelected"
            @change="toggleSelectAll"
            :data-testid="`${testId}__select-all`"
          >
            <template v-slot:label>
              <span class="font-size-md">Select all ({{ filteredOptions.length }})</span>
            </template>
          </v-checkbox>
        </div>
        <div
          class="search-checkbox-picker__scroll"
          :data-testid="`${testId}__scroll`"
        >
          <v-virtual-scroll
            v-if="filteredOptions.length > 0"
            :items="filteredOptions"
            :item-height="40"
            height="240"
            bench="8"
          >
            <template v-slot="{ item }">
              <v-checkbox
                :key="item.value"
                dense
                hide-details
                class="px-2 mt-0 pt-0"
                :input-value="isPending(item.value)"
                @change="toggleValue(item.value)"
                :data-testid="`${testId}__option-${item.value}`"
              >
                <template v-slot:label>
                  <span class="font-size-md noTransform">{{ item.text }}</span>
                </template>
              </v-checkbox>
            </template>
          </v-virtual-scroll>
          <div
            v-else
            class="text-center py-4 grey--text"
            :data-testid="`${testId}__empty`"
          >
            No results found
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <base-button
          v-if="pendingValues.length"
          secondary
          small
          label="Clear"
          @click="clearPending"
          :data-testid="`${testId}__clear`"
        />
        <v-spacer />
        <base-button
          primary
          small
          :label="applyLabel"
          @click="onApply"
          :data-testid="`${testId}__apply`"
        />
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { normalizePickerId, pickerIdsEqual } from '@/modules/varianceanalysis/utils/aiContextHelpers';

export default {
  name: 'SearchCheckboxPickerMenu',
  props: {
    title: { type: String, default: 'Select' },
    options: { type: Array, default: () => [] },
    selectedValues: { type: Array, default: () => [] },
    applyLabel: { type: String, default: 'Apply' },
    activatorLabel: { type: String, default: 'Add' },
    showSelectAll: { type: Boolean, default: true },
    /** Optional module chips e.g. Profit & Loss / Balance Sheet */
    moduleFilters: { type: Array, default: () => [] },
    moduleFilterValue: { type: String, default: '' },
    testId: { type: String, default: 'search-checkbox-picker' },
  },
  data() {
    return {
      menuOpen: false,
      searchText: '',
      pendingValues: [],
      moduleFilter: this.moduleFilterValue || (this.moduleFilters[0] && this.moduleFilters[0].value) || '',
    };
  },
  computed: {
    filteredOptions() {
      const term = (this.searchText || '').toLowerCase().trim();
      if (!term) return this.options;
      return this.options.filter((opt) =>
        (opt.text || '').toLowerCase().includes(term) ||
        (opt.value && opt.value.toString().toLowerCase().includes(term))
      );
    },
    allSelected() {
      if (!this.filteredOptions.length) return false;
      return this.filteredOptions.every((opt) => this.isPending(opt.value));
    },
    selectAllIndeterminate() {
      const selectedCount = this.filteredOptions.filter((opt) => this.isPending(opt.value)).length;
      return selectedCount > 0 && selectedCount < this.filteredOptions.length;
    },
  },
  watch: {
    menuOpen(isOpen) {
      if (isOpen) {
        this.pendingValues = (this.selectedValues || []).slice();
        this.searchText = '';
        if (this.moduleFilters.length && !this.moduleFilter) {
          this.moduleFilter = this.moduleFilters[0].value;
        }
        this.$emit('open');
      }
    },
    moduleFilterValue(val) {
      if (val) this.moduleFilter = val;
    },
  },
  methods: {
    isPending(value) {
      return this.pendingValues.some((pending) => pickerIdsEqual(pending, value));
    },
    toggleValue(value) {
      const idx = this.pendingValues.findIndex((pending) => pickerIdsEqual(pending, value));
      if (idx > -1) {
        this.pendingValues.splice(idx, 1);
      } else {
        this.pendingValues.push(value);
      }
    },
    toggleSelectAll() {
      const vals = this.filteredOptions.map((o) => o.value);
      const shouldSelectAll = !this.allSelected;
      if (shouldSelectAll) {
        const merged = [...this.pendingValues];
        vals.forEach((val) => {
          if (!merged.some((pending) => pickerIdsEqual(pending, val))) {
            merged.push(val);
          }
        });
        this.pendingValues = merged;
      } else {
        this.pendingValues = this.pendingValues.filter(
          (pending) => !vals.some((val) => pickerIdsEqual(pending, val))
        );
      }
    },
    onModuleFilterChange(value) {
      if (this.moduleFilter === value) return;
      this.moduleFilter = value;
      this.searchText = '';
      this.$emit('update:moduleFilterValue', value);
      this.$emit('module-filter-change', value);
    },
    clearPending() {
      this.pendingValues = [];
    },
    closeMenu() {
      this.menuOpen = false;
    },
    onApply() {
      const selected = [];
      const seen = new Set();
      this.pendingValues.forEach((pending) => {
        const key = normalizePickerId(pending);
        if (!key || seen.has(key)) return;
        const opt = this.options.find((option) => pickerIdsEqual(option.value, pending));
        if (opt) {
          seen.add(key);
          selected.push(opt);
        }
      });
      // Emit full current selection (supports deselect / clear)
      this.$emit('apply', selected);
      this.menuOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './SearchCheckboxPickerMenu.scss';
</style>
